// =====================================================================
// Indian currency input helpers
// =====================================================================
// One source of truth for:
//   * grouping a (possibly partial) numeric string Indian-style, e.g.
//     "2000000" -> "20,00,000" — tolerant of trailing dots / decimals so
//     it can format live while the user is still typing.
//   * a short human hint shown under amount fields, e.g. ₹20 L / ₹2 Cr /
//     ₹20 K, so users can sanity-check large numbers at a glance.
// =====================================================================

/**
 * Group a numeric string Indian-style for display inside an input.
 * Keeps decimals and a trailing dot intact so typing isn't disrupted.
 */
export function groupIndianInput(value) {
  if (value === null || value === undefined) return "";

  let s = String(value).replace(/,/g, "");
  if (s === "") return "";

  const negative = s.startsWith("-");
  if (negative) s = s.slice(1);

  const dotIndex = s.indexOf(".");
  let intPart = dotIndex === -1 ? s : s.slice(0, dotIndex);
  const decPart = dotIndex === -1 ? null : s.slice(dotIndex + 1).replace(/\./g, "");

  // digits only, strip leading zeros (but keep a lone 0)
  intPart = intPart.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

  let grouped;
  if (intPart.length <= 3) {
    grouped = intPart;
  } else {
    const last3 = intPart.slice(-3);
    const rest = intPart.slice(0, -3);
    grouped = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
  }

  let out = grouped;
  if (decPart !== null) out += "." + decPart;

  return (negative ? "-" : "") + out;
}

/** Strip grouping commas, returning a raw numeric string (for state). */
export function stripGrouping(value) {
  if (value === null || value === undefined) return "";
  return String(value).replace(/,/g, "");
}

const trimNum = (x) => {
  const r = Math.round(x * 100) / 100;
  return String(r);
};

/**
 * Short Indian hint for an amount field: ₹20 L / ₹2 Cr / ₹20 K.
 * Returns "" for empty / sub-thousand values (no abbreviation needed).
 */
export function shortINRHint(value) {
  if (value === null || value === undefined || value === "") return "";

  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n) || n === 0) return "";

  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";

  if (abs >= 1e7) return `${sign}₹${trimNum(abs / 1e7)} Cr`;
  if (abs >= 1e5) return `${sign}₹${trimNum(abs / 1e5)} L`;
  if (abs >= 1e3) return `${sign}₹${trimNum(abs / 1e3)} K`;

  return ""; // below ₹1,000 the grouped field is already self-explanatory
}

/**
 * Full grouped rupee display, e.g. ₹20,00,000. Returns "—" for empty values.
 */
export function formatINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

/**
 * Compact premium rupee display that always abbreviates large numbers:
 * ₹22 K / ₹22 L / ₹22 Cr, falling back to grouped ₹ for sub-thousand values.
 * Returns "—" for empty values.
 */
export function compactINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "—";

  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";

  if (abs >= 1e7) return `${sign}₹${trimNum(abs / 1e7)} Cr`;
  if (abs >= 1e5) return `${sign}₹${trimNum(abs / 1e5)} L`;
  if (abs >= 1e3) return `${sign}₹${trimNum(abs / 1e3)} K`;

  return `${sign}₹${abs.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}
